import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '@/integrations/supabase/client';
import type { Client, ClientMember } from '@/types/client';

export function useClientAuth() {
  const { user } = useAuth();
  const [clients, setClients] = useState<Client[]>([]);
  const [memberships, setMemberships] = useState<ClientMember[]>([]);
  const [currentClient, setCurrentClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchClientsAndMemberships = async () => {
    if (!user) {
      setClients([]);
      setMemberships([]);
      setCurrentClient(null);
      setLoading(false);
      return;
    }

    try {
      // Fetch user's memberships
      const { data: membershipData, error: membershipError } = await supabase
        .from('client_members')
        .select('*')
        .eq('user_id', user.id);

      if (membershipError) throw membershipError;

      setMemberships(membershipData as ClientMember[] || []);

      // Fetch only clients where user has membership
      if (membershipData && membershipData.length > 0) {
        const clientIds = membershipData.map(m => m.client_id);
        const { data: clientData, error: clientError } = await supabase
          .from('clients')
          .select('*')
          .in('id', clientIds)
          .order('created_at', { ascending: false });

        if (clientError) throw clientError;

        // Transform database clients to match interface
        const transformedClients = (clientData || []).map(client => ({
          ...client,
          marketplace: client.marketplaces?.[0] || 'FR',
        }));
        setClients(transformedClients);

        // Set current client from localStorage or first client
        const savedClientSlug = localStorage.getItem('currentClientSlug');
        if (savedClientSlug && transformedClients.length > 0) {
          const savedClient = transformedClients.find(c => c.slug === savedClientSlug);
          if (savedClient) {
            setCurrentClient(savedClient);
          } else if (transformedClients.length > 0) {
            setCurrentClient(transformedClients[0]);
            localStorage.setItem('currentClientSlug', transformedClients[0].slug);
          }
        } else if (transformedClients.length > 0) {
          setCurrentClient(transformedClients[0]);
          localStorage.setItem('currentClientSlug', transformedClients[0].slug);
        }
      } else {
        setClients([]);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const switchClient = (clientSlug: string) => {
    const client = clients.find(c => c.slug === clientSlug);
    if (client) {
      setCurrentClient(client);
      localStorage.setItem('currentClientSlug', clientSlug);
    }
  };

  const hasRole = (role: 'owner' | 'manager' | 'viewer'): boolean => {
    if (!currentClient || !user) return false;
    const membership = memberships.find(m => 
      m.client_id === currentClient.id && m.user_id === user.id
    );
    return membership?.role === role;
  };

  const canManage = (): boolean => {
    return hasRole('owner') || hasRole('manager');
  };

  const isOwner = (): boolean => {
    return hasRole('owner');
  };

  useEffect(() => {
    fetchClientsAndMemberships();
  }, [user]);

  return {
    clients,
    currentClient,
    memberships,
    loading,
    switchClient,
    hasRole,
    canManage,
    isOwner,
    refetch: fetchClientsAndMemberships
  };
}